/**
 * Helper for tracking NoteTelemetry events to Statsig from non-React contexts
 * This module provides a way to track note telemetry events to Statsig without using React hooks
 */

import { flattenNoteTelemetryPayload } from '@utils/analytics/flattenNoteTelemetryPayload';
import type {
  EditEventMetrics,
  NoteTelemetryEventType,
  NoteViewedMetrics,
  SectionEventMetrics,
  SelectionMetrics,
} from '@utils/analytics/noteTelemetry';

import { EventNames } from './constants';

// Store reference to Statsig client when initialized
let statsigClientInstance: {
  logEvent: (name: string, value?: number, metadata?: Record<string, string>) => void;
  checkGate: (gateName: string) => boolean;
  flush?: () => Promise<void>;
} | null = null;

/**
 * Get the Statsig client instance for non-React event logging (e.g., beforeunload handlers).
 */
export function getStatsigClientInstance() {
  return statsigClientInstance;
}

/**
 * Set the Statsig client instance for use in non-React contexts
 * This should be called during app initialization after StatsigProvider is set up
 */
export function setStatsigClientInstance(client: {
  logEvent: (name: string, value?: number, metadata?: Record<string, string>) => void;
  checkGate: (gateName: string) => boolean;
  flush?: () => Promise<void>;
}) {
  statsigClientInstance = client;
}

/**
 * Track a NoteTelemetry event to Statsig
 * Maps NoteTelemetry event types to Statsig event names and flattens payloads
 * Safe to call even if Statsig is not initialized - will fail silently
 */
export function trackNoteTelemetryToStatsig(
  type: NoteTelemetryEventType,
  payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
): void {
  try {
    // Early return if Statsig client is not initialized
    if (!statsigClientInstance) {
      if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev') {
        console.debug('[NoteTelemetry → Statsig] Client not initialized, skipping event:', type);
      }
      return;
    }

    // Flatten the payload for Statsig compatibility
    const flattenedPayload = flattenNoteTelemetryPayload(payload);

    // Map NoteTelemetry event type to Statsig event name
    let statsigEventName: string;
    switch (type) {
      case 'selection_update':
        statsigEventName = EventNames.NOTE_SELECTION_UPDATE;
        break;
      case 'text_selected':
        statsigEventName = EventNames.NOTE_TEXT_SELECTED;
        break;
      case 'text_copied':
        statsigEventName = EventNames.NOTE_TEXT_COPIED;
        break;
      case 'note_viewed':
        statsigEventName = EventNames.NOTE_NOTE_VIEWED;
        break;
      case 'section_rendered':
        statsigEventName = EventNames.NOTE_SECTION_RENDERED;
        break;
      case 'section_interacted':
        statsigEventName = EventNames.NOTE_SECTION_INTERACTED;
        break;
      case 'section_edited':
        statsigEventName = EventNames.NOTE_SECTION_EDITED;
        break;
      default:
        console.warn('[NoteTelemetry → Statsig] Unknown event type:', type);
        return;
    }

    // Track to Statsig with flattened metadata
    statsigClientInstance.logEvent(statsigEventName, undefined, flattenedPayload);

    // Check if analytics logging is enabled via feature gate
    const showAnalyticsLogs = statsigClientInstance.checkGate('feature_show_analytics_logs');

    // Log in development for debugging
    if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev' || showAnalyticsLogs) {
      console.debug('[NoteTelemetry → Statsig] Event tracked:', {
        originalType: type,
        statsigEvent: statsigEventName,
        metadata: flattenedPayload,
      });
    }
  } catch (error) {
    // Fail silently - telemetry errors should not break the app or NoteTelemetry
    console.error('[NoteTelemetry → Statsig] Failed to track event:', error);
  }
}
